import { Alert } from '@mui/material';
import { useMutation, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { useTRPC } from '#src/trpc.js';
import { ComponentInferenceTable } from './component-inference-table.js';
import { csvRowsToGridRows, gridRowsToCsvRows } from './csv-row-transforms.js';
import { ExportDialog } from './export-dialog.js';

import type { ComponentInferenceRow, CsvRow } from './csv-row-transforms.js';

export const Route = createFileRoute(
	'/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId/component-inference/$referenceDesignator',
)({
	component: ComponentInferencePage,
});

function ComponentInferencePage() {
	const { revisionId, referenceDesignator } = Route.useParams();
	const trpc = useTRPC();

	// Bootstrap fetches in parallel:
	//   - savedRows: any previously persisted draft (or null), so we can skip
	//     the LLM-heavy inference call when a draft exists.
	//   - buildOwner: server-derived ownerId for the export-to-package step;
	//     resolving server-side avoids trusting a client-supplied value.
	const [{ data: savedRows }, { data: buildOwner }] = useSuspenseQueries({
		queries: [
			trpc.unspecifiedComponents.getSavedRows.queryOptions({
				buildId: revisionId,
				referenceDesignator,
			}),
			trpc.unspecifiedComponents.getBuildOwner.queryOptions({
				buildId: revisionId,
			}),
		],
	});

	if (savedRows !== null) {
		return (
			<ComponentInferenceEditor
				revisionId={revisionId}
				referenceDesignator={referenceDesignator}
				initialRows={savedRows}
				ownerId={buildOwner.ownerId}
			/>
		);
	}

	return (
		<FreshInferenceLoader
			revisionId={revisionId}
			referenceDesignator={referenceDesignator}
			ownerId={buildOwner.ownerId}
		/>
	);
}

interface FreshInferenceLoaderProps {
	revisionId: string;
	referenceDesignator: string;
	ownerId: string;
}

function FreshInferenceLoader({ revisionId, referenceDesignator, ownerId }: FreshInferenceLoaderProps) {
	const trpc = useTRPC();
	const { data: inferredData } = useSuspenseQuery(
		trpc.unspecifiedComponents.inferProperties.queryOptions({
			buildId: revisionId,
			referenceDesignator,
		}),
	);

	return (
		<ComponentInferenceEditor
			revisionId={revisionId}
			referenceDesignator={referenceDesignator}
			initialRows={inferredData.rows}
			ownerId={ownerId}
		/>
	);
}

interface ComponentInferenceEditorProps {
	revisionId: string;
	referenceDesignator: string;
	initialRows: CsvRow[];
	ownerId: string;
}

function ComponentInferenceEditor({
	revisionId,
	referenceDesignator,
	initialRows,
	ownerId,
}: ComponentInferenceEditorProps) {
	const trpc = useTRPC();
	const navigate = useNavigate();

	const [validationError, setValidationError] = useState<string | null>(null);
	const [exportDialogOpen, setExportDialogOpen] = useState(false);
	// Single source of truth for grid rows. The table is a controlled component;
	// we own the state and convert to/from wire format only at API boundaries.
	const [rows, setRows] = useState<ComponentInferenceRow[]>(() => csvRowsToGridRows(initialRows));

	const { mutate: saveDraft, isPending: isSaving } = useMutation(
		trpc.unspecifiedComponents.saveRows.mutationOptions({
			onSuccess: () => {
				setValidationError(null);
			},
			onError: (err) => {
				setValidationError(err.message);
			},
		}),
	);

	const { mutate: refreshDownstream, isPending: isRefreshing } = useMutation(
		trpc.unspecifiedComponents.regenerateInferences.mutationOptions({
			onSuccess: (result) => {
				setRows(csvRowsToGridRows(result.rows));
				setValidationError(null);
			},
			onError: (err) => {
				setValidationError(err.message);
			},
		}),
	);

	const { mutateAsync: exportInferredComponent } = useMutation(
		trpc.unspecifiedComponents.exportInferredComponent.mutationOptions(),
	);

	const { mutateAsync: createPackage } = useMutation(trpc.package.createFromComponent.mutationOptions());

	const handleSave = useCallback(() => {
		setValidationError(null);
		saveDraft({
			buildId: revisionId,
			referenceDesignator,
			rows: gridRowsToCsvRows(rows),
		});
	}, [saveDraft, revisionId, referenceDesignator, rows]);

	const handleRefreshInferences = useCallback(() => {
		setValidationError(null);
		refreshDownstream({ rows: gridRowsToCsvRows(rows) });
	}, [refreshDownstream, rows]);

	const handleExport = useCallback(
		async (mpn: string, packageFamilyName?: string) => {
			const { componentWithBoundaries } = await exportInferredComponent({
				rows: gridRowsToCsvRows(rows),
			});

			const result = await createPackage({
				component: componentWithBoundaries,
				ownerId,
				manufacturerPartNumber: mpn,
				...(packageFamilyName ? { packageFamilyName } : {}),
			});

			void navigate({
				to: '/package-store/$packageFamilyId/packages/$packageId/general',
				params: {
					packageFamilyId: result.package.packageFamilyId,
					packageId: result.package.id,
				},
			});
		},
		[exportInferredComponent, createPackage, rows, navigate, ownerId],
	);

	return (
		<div className='flex h-full flex-col'>
			{validationError && (
				<Alert severity='error' onClose={() => setValidationError(null)} className='m-1'>
					{validationError}
				</Alert>
			)}

			<div className='min-h-0 flex-1'>
				<ComponentInferenceTable
					rows={rows}
					onRowsChange={setRows}
					onSave={handleSave}
					onRefreshInferences={handleRefreshInferences}
					onExport={() => setExportDialogOpen(true)}
					isSaving={isSaving}
					isRefreshing={isRefreshing}
				/>
			</div>

			<ExportDialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} onExport={handleExport} />
		</div>
	);
}
